import { GenericResponse } from "@/common/common_utils";
import { ENDPOINTS } from "@/constants/endpoints";
import { axiosInstance } from "@/services/axios.service";
import { useRef, useState } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";

export interface BulkProcessRequest {
	OperationType: string;
	FileUrl: string;
}
export interface UploadEmployeeFailedObject {
	Phone: string;
	Reason: string;
}
export interface UploadEmployeeInviteLink {
	EmployeeId: number;
	Link: string;
}

export interface UploadEmployeesResponse {
	ReadRowsCount: number;
	PersistRowsCount: number;
	FailedRiders: UploadEmployeeFailedObject[];
	SuccessRiderIds: number[];
	InviteLinks: UploadEmployeeInviteLink[];
}
// Form-agnostic control that can be used with or without forms
type FileUploadControlProps = {
	value?: string;
	onChange?: (value: string | undefined) => void;
	isError?: boolean;
	errorMessage?: string;
	uploadEndpoint: string;
	accept: string;
	title: string;
};

const FileUploadControl = (props: FileUploadControlProps) => {

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [uploadedFileUrl, setUploadedFileUrl] = useState<string | undefined>();
	const [isDragOver, setIsDragOver] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	type UploadFileResponse = { FileId?: string; fileId?: string; FileName?: string; fileName?: string; Url?: string; url?: string };

	const startUpload = async (file: File | null) => {
		setSelectedFile(file);
		setUploadedFileUrl(undefined);
		setUploadError(null);
		setUploadProgress(0);
		if (!file) return;

		// Abort any in-flight upload
		abortControllerRef.current?.abort();
		const controller = new AbortController();
		abortControllerRef.current = controller;

		const fd = new FormData();
		fd.append('formFile', file, file.name);
		setUploading(true);
		try {
			const res = await axiosInstance.post<GenericResponse<UploadFileResponse>>(
				props.uploadEndpoint,
				//ENDPOINTS['/api/X4bConcierge/UploadLostItemFile'],
				fd,
				{
					headers: { 'Content-Type': 'multipart/form-data' },
					signal: controller.signal,
					onUploadProgress: (evt) => {
						if (!evt.total) return;
						const percent = Math.round((evt.loaded * 100) / evt.total);
						setUploadProgress(percent);
					},
				}
			);
			const data = res.data?.Data ?? (res.data as any)?.data?.Data;
			const fileUrl = data?.Url ?? undefined;
			setUploadedFileUrl(fileUrl);
			if (fileUrl) {
				props.onChange?.(fileUrl);
			} else {
				props.onChange?.(undefined);
			}
		} catch (err: any) {
			if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
				// no-op on cancel
			} else {
				setUploadError('Upload failed');
			}
		} finally {
			setUploading(false);
			abortControllerRef.current = null;
		}
	};

	// We'll handle form value updates directly in the upload handlers

	return (
		<>
			{!uploadedFileUrl && (
				<div>
					{/* Hidden input */}
					<input
						ref={fileInputRef}
						type="file"
						accept={props.accept}
						className="hidden"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							const f = e.target.files?.[0] ?? null;
							void startUpload(f);
						}}
					/>

					{/* Idle / Progress / Error states */}
					{uploadError ? (
						<div
							className="flex flex-col items-center justify-center h-40 rounded-2xl border-2 border-dashed border-error bg-error/10 text-onSurface gap-3"
						>
							<div className="text-onSurface">There was a problem uploading the file</div>
							<button
								type="button"
								className="text-primary font-medium"
								onClick={() => {
									setUploadError(null);
									setUploadProgress(0);
								}}
							>
								Try Again
							</button>
						</div>
					) : uploading ? (
						<div
							className="flex flex-col items-center justify-center h-40 rounded-2xl border-2 border-dashed border-tertiary bg-primary100 text-tertiary gap-3 px-4"
						>
							<div className="w-1/2 h-1 rounded-full bg-primaryContainer overflow-hidden">
								<div
									className="h-full bg-tertiary transition-all"
									style={{ width: `${uploadProgress}%` }}
								/>
							</div>
							<div className="text-onSurfaceVariant">{uploadProgress}% Complete</div>
							<button
								type="button"
								className="text-error font-medium"
								onClick={() => {
									abortControllerRef.current?.abort();
								}}
							>
								Cancel
							</button>
						</div>
					) : (
						<div
							className={[
								"flex flex-col items-center justify-center h-40 rounded-2xl",
								"border-2 border-dashed error2",
								"text-onSurfaceVariant gap-3",
								props.isError
									? "border-error"
									: isDragOver ? "border-tertiary" : "border-tertiary",
								props.isError ?
									"bg-red-50" : "bg-surfaceContainer"
							].join(" ")}
							onDragOver={(e) => {
								e.preventDefault();
								setIsDragOver(true);
							}}
							onDragLeave={() => setIsDragOver(false)}
							onDrop={(e) => {
								e.preventDefault();
								setIsDragOver(false);
								const f = e.dataTransfer.files?.[0] ?? null;
								void startUpload(f);
							}}
						>
							<div className="mb-1">
								{props.title}
							</div>
							<button
								type="button"
								className="rounded-full bg-secondaryContainer text-onSurface px-4 py-2 font-medium shadow"
								onClick={() => fileInputRef.current?.click()}
							>
								Browse CSV file
							</button>
						</div>
					)}
				</div>
			)}

			{uploadedFileUrl && (
				<div className="mt-3">
					<div className="inline-flex items-center gap-2 rounded-lg border border-outline bg-transparent px-3 py-1 text-body-large text-onSurfaceVariant">
						<span className="font-medium">
							{selectedFile?.name ?? `${uploadedFileUrl}.jpg`}
						</span>
						<button
							type="button"
							className="text-neutral40 hover:text-onSurface"
							onClick={() => {
								// TODO: remove the file from backend
								setUploadedFileUrl(undefined);
								setSelectedFile(null);
								props.onChange?.(undefined);
							}}
							aria-label="Remove file"
						>
							×
						</button>
					</div>
				</div>
			)}

			{/* Show file upload error if no file is uploaded */}
			{props.errorMessage && (
				<div className="mt-2 text-error text-body-small">
					{props.errorMessage}
				</div>
			)}
		</>
	);
};
/* This is recommended approach of making controls.
 1. Make the control without forms.
2. Make thin wrapper around it for forms. Use useController, not Controller control.
3. DO NOT do specialized control for form. All details should go to the non-4-form control. Form control is just thin wrapper with only one purpose - adapt for form.
See @docs/cursor_using_form_controls_outside_of_forms.md for a discussion about that.
*/
const FileUpload4Form = <T extends FieldValues>(props: FileUploadControlProps & { control: Control<T> }) => {
	const { field, fieldState } = useController({
		control: props.control,
		name: "FileUrl" as Path<T>,
		rules: {
			required: 'Please upload an image of the lost item',
			min: {
				value: 1,
				message: 'Please upload an image of the lost item'
			}
		}
	});

	return (
		<>
			{/* hidden field for validation to work simultaneously with form validation*/}
			<input type="hidden" {...field} />
			<FileUploadControl
				value={field.value}
				onChange={(v) => field.onChange(v)}
				isError={!!fieldState.error}
				errorMessage={fieldState.error?.message}
				uploadEndpoint={props.uploadEndpoint}
				accept={props.accept}
				title={props.title}
			/>
		</>
	);
};

export default FileUpload4Form;
export { FileUploadControl };
